import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
interface PwaState {
  update: ServiceWorker | null;
  installed: boolean;
  standalone: boolean;
  canInstall: boolean;
  install: () => Promise<void>;
  error: string;
}
const Pwa = createContext<PwaState>(null!);
export function PwaProvider({ children }: { children: ReactNode }) {
  const [update, setUpdate] = useState<ServiceWorker | null>(null),
    [prompt, setPrompt] = useState<InstallPrompt | null>(null),
    [installed, setInstalled] = useState(
      matchMedia("(display-mode: standalone)").matches ||
        !!(navigator as Navigator & { standalone?: boolean }).standalone,
    ),
    [error, setError] = useState("");
  useEffect(() => {
    const before = (event: Event) => {
      event.preventDefault();
      setPrompt(event as InstallPrompt);
    };
    const done = () => {
      setInstalled(true);
      setPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", before);
    window.addEventListener("appinstalled", done);
    let controlled = !!navigator.serviceWorker?.controller;
    const change = () => {
      if (controlled) location.reload();
      controlled = true;
    };
    if (import.meta.env.PROD && "serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("controllerchange", change);
      void navigator.serviceWorker
        .register("/sw.js", { updateViaCache: "none" })
        .then((reg) => {
          if (reg.waiting) setUpdate(reg.waiting);
          reg.addEventListener("updatefound", () => {
            const worker = reg.installing;
            worker?.addEventListener("statechange", () => {
              if (
                worker.state === "installed" &&
                navigator.serviceWorker.controller
              )
                setUpdate(worker);
            });
          });
        })
        .catch(() =>
          setError(
            "Offline app setup could not finish. Reopen the app while connected to try again.",
          ),
        );
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", before);
      window.removeEventListener("appinstalled", done);
      navigator.serviceWorker?.removeEventListener("controllerchange", change);
    };
  }, []);
  useEffect(() => {
    const viewport = window.visualViewport;
    const sync = () => {
      document.documentElement.style.setProperty(
        "--visual-height",
        `${viewport?.height || innerHeight}px`,
      );
      document.documentElement.style.setProperty(
        "--visual-top",
        `${viewport?.offsetTop || 0}px`,
      );
    };
    sync();
    viewport?.addEventListener("resize", sync);
    viewport?.addEventListener("scroll", sync);
    window.addEventListener("resize", sync);
    return () => {
      viewport?.removeEventListener("resize", sync);
      viewport?.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
  }, []);
  async function install() {
    if (!prompt) return;
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
      setPrompt(null);
    } catch {
      setError(
        "Installation could not finish. Try your browser’s install menu.",
      );
    }
  }
  return (
    <Pwa.Provider
      value={{
        update,
        installed,
        standalone:
          matchMedia("(display-mode: standalone)").matches ||
          !!(navigator as Navigator & { standalone?: boolean }).standalone,
        canInstall: !!prompt,
        install,
        error,
      }}
    >
      {children}
    </Pwa.Provider>
  );
}
export const usePwa = () => useContext(Pwa);
